import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  InputBase,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Box,
  styled,
  alpha,
  useTheme,
  Tooltip,
  Popover
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MessageIcon from '@mui/icons-material/Message';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'; // Import do Calendário
import SettingsIcon from '@mui/icons-material/Settings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import EmailIcon from '@mui/icons-material/Email';
import { useSearch } from '../contexts/SearchContext';
import { useAuth } from '../hooks/useAuth';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import SettingsModal from '../components/SettingsModal';
import AgendaComponent from '../components/AgendaComponent'; // Import do Componente
import { useSocket } from '../contexts/SocketContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// Função para criar estilos de background reutilizáveis
const createBackgroundStyles = (theme, normalOpacity1, normalOpacity2, hoverOpacity1, hoverOpacity2) => {
  const getThemeBasedAlpha = (opacity1, opacity2) =>
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, opacity1)
      : alpha(theme.palette.common.white, opacity2);

  return {
    backgroundColor: getThemeBasedAlpha(normalOpacity1, normalOpacity2),
    '&:hover': {
      backgroundColor: getThemeBasedAlpha(hoverOpacity1, hoverOpacity2),
    },
  };
};

// Estilos customizados para a barra de pesquisa
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius * 3, // Mais arredondado
  ...createBackgroundStyles(theme, 0.1, 0.15, 0.2, 0.25),
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

function Header({ onToggleSidebar, sidebarOpen }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const theme = useTheme();

  // Usar o hook de autenticação
  const { user: usuario, logout: handleLogout } = useAuth();

  // Usar o contexto de preferências do usuário
  const { userPhoto, updateUserPhoto } = useUserPreferences();

  const { searchTerm, updateSearchTerm, clearSearch } = useSearch();
  const [openSettings, setOpenSettings] = React.useState(false);

  const { unreadCount, resetUnread } = useSocket();
  const navigate = useNavigate();

  // State para Agenda Popover
  const [agendaAnchorEl, setAgendaAnchorEl] = React.useState(null);
  const openAgenda = Boolean(agendaAnchorEl);

  const handleAgendaClick = (event) => {
    setAgendaAnchorEl(event.currentTarget);
  };
  const handleAgendaClose = () => {
    setAgendaAnchorEl(null);
  };

  // State para contagem de eventos hoje
  const [eventosHojeCount, setEventosHojeCount] = React.useState(0);

  const fetchAgendaHoje = React.useCallback(async () => {
    if (!usuario) return;
    try {
      const hoje = new Date();
      const mes = hoje.getMonth() + 1;
      const ano = hoje.getFullYear();
      // Ajuste para fuso horário local
      const offset = hoje.getTimezoneOffset() * 60000;
      const localDate = new Date(hoje.getTime() - offset);
      const hojeString = localDate.toISOString().split('T')[0];

      const res = await api.get(`/agenda?mes=${mes}&ano=${ano}`);
      if (res.data.status === 'sucesso') {
        const count = res.data.dados.filter(e => e.dt_evento === hojeString).length;
        setEventosHojeCount(count);
      }
    } catch (error) {
      setEventosHojeCount(0);
      // A agenda é um recurso auxiliar e independente do painel. Uma falha nela
      // não deve interromper a sessão nem poluir o console de produção.
      if (process.env.NODE_ENV === 'development') {
        console.warn('Não foi possível carregar a agenda pessoal:', error);
      }
    }
  }, [usuario]);

  React.useEffect(() => {
    fetchAgendaHoje();
  }, [fetchAgendaHoje]);

  // ===== CONSTANTES E FUNÇÕES BASE (definidas primeiro) =====

  // Constantes reutilizáveis para valores comuns
  const COMMON_VALUES = {
    smallIconSize: '12px',
    smallOpacity: 0.8,
    avatarSize: 32,
    standardMargin: 2,
    standardPadding: 1,
    smallPadding: 0.5,
    standardBorderRadius: 1,
    tooltipBorderRadius: 2,
    tooltipFontSize: '12px',
    tooltipFontWeight: 500,
    fullWidth: '100%',
    zeroMinWidth: 0,
    clearButtonRight: 8,
    clearButtonTop: '50%',
    clearButtonTransform: 'translateY(-50%)',
    smallMarginBottom: 0.2,
    disabledOpacity: 0.6,
    defaultCursor: 'default',
    transparentBackground: 'transparent',
    iconMargin: 1,
    menuVertical: 'bottom',
    menuHorizontal: 'right'
  };

  // Função reutilizável para lógica de tema dark/light
  const getThemeBasedValue = (darkValue, lightValue) =>
    theme.palette.mode === 'dark' ? darkValue : lightValue;

  // ===== FUNÇÕES DE ESTILO (definidas segundo) =====

  const getBackgroundColor = (opacity1, opacity2) =>
    getThemeBasedValue(
      alpha(theme.palette.common.white, opacity1),
      alpha(theme.palette.common.white, opacity2)
    );

  // Função para obter cores baseadas no tema
  const getThemeColor = (darkColor, lightColor) =>
    getThemeBasedValue(darkColor, lightColor);

  // Função para hover background padrão
  const getHoverBackground = () => 'rgba(255, 255, 255, 0.1)';

  // Funções reutilizáveis para backgrounds comuns
  const getStandardBackground = () => getBackgroundColor(0.08, 0.1);
  const getStandardHoverBackground = () => getBackgroundColor(0.12, 0.15);
  const getButtonHoverBackground = () => getBackgroundColor(0.15, 0.2);

  // Função reutilizável para Box com display flex
  const getFlexBoxStyles = (gap = 1) => ({
    display: 'flex',
    alignItems: 'center',
    gap
  });

  // Função reutilizável para Box com display flex column
  const getFlexColumnStyles = (alignItems = 'flex-start') => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems
  });

  // Função reutilizável para posicionamento absoluto centralizado
  const getAbsoluteCenterStyles = () => ({
    position: 'absolute',
    right: COMMON_VALUES.clearButtonRight,
    top: COMMON_VALUES.clearButtonTop,
    transform: COMMON_VALUES.clearButtonTransform
  });

  // Estilos reutilizáveis para Typography
  const getTextEllipsisStyles = (fontSize, fontWeight, opacity = 1) => ({
    color: 'inherit',
    opacity,
    fontSize,
    fontWeight,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  });

  // Função para obter estilos do AppBar
  const getAppBarStyles = () => ({
    zIndex: (theme) => theme.zIndex.drawer + 1,
    backgroundColor: getThemeColor(theme.palette.background.paper, theme.palette.primary.main),
    color: getThemeColor(theme.palette.text.primary, theme.palette.primary.contrastText),
    boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)'
  });

  // Estilos reutilizáveis para evitar duplicação
  const getTooltipStyles = () => ({
    '& .MuiTooltip-tooltip': {
      backgroundColor: getThemeBasedValue('rgba(0, 0, 0, 0.9)', 'rgba(255, 255, 255, 0.95)'),
      color: getThemeBasedValue('#fff', '#000'),
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      border: `1px solid ${getThemeBasedValue('rgba(255, 255, 255, 0.1)', 'rgba(0, 0, 0, 0.1)')}`,
      borderRadius: COMMON_VALUES.tooltipBorderRadius,
      fontSize: COMMON_VALUES.tooltipFontSize,
      fontWeight: COMMON_VALUES.tooltipFontWeight
    }
  });

  const getDisabledIconButtonStyles = () => ({
    opacity: COMMON_VALUES.disabledOpacity,
    cursor: COMMON_VALUES.defaultCursor,
    '&:hover': {
      backgroundColor: COMMON_VALUES.transparentBackground,
      opacity: COMMON_VALUES.smallOpacity
    }
  });

  // ===== FUNÇÕES DE RENDERIZAÇÃO (definidas terceiro) =====

  // Função reutilizável para MenuItem com ícone
  const renderMenuItemWithIcon = (icon, text, onClick, iconColor = 'inherit') => (
    <MenuItem onClick={onClick}>
      {icon && React.cloneElement(icon, {
        sx: {
          mr: COMMON_VALUES.iconMargin,
          color: iconColor
        }
      })}
      {text}
    </MenuItem>
  );

  // Função reutilizável para botões de notificação/mensagem
  const renderNotificationButton = (icon, ariaLabel, iconColor = 'inherit') => (
    <Tooltip
      title="Ainda não implementado"
      placement="bottom"
      arrow
      sx={getTooltipStyles()}
    >
      <IconButton
        size="large"
        aria-label={ariaLabel}
        color="inherit"
        sx={{
          ...getDisabledIconButtonStyles(),
          '& .MuiSvgIcon-root': {
            color: iconColor
          }
        }}
      >
        <Badge badgeContent={0} color="error">
          {icon}
        </Badge>
      </IconButton>
    </Tooltip>
  );

  // Função reutilizável para configurações do Menu
  const getMenuConfig = () => ({
    anchorOrigin: {
      vertical: COMMON_VALUES.menuVertical,
      horizontal: COMMON_VALUES.menuHorizontal,
    },
    keepMounted: true,
    transformOrigin: {
      vertical: COMMON_VALUES.menuVertical,
      horizontal: COMMON_VALUES.menuHorizontal,
    },
    open: Boolean(anchorEl),
    onClose: handleClose
  });

  // Função para atualizar foto (callback do SettingsModal)
  const handlePhotoUpdate = (newPhotoData) => {
    // Normaliza e atualiza via contexto
    updateUserPhoto(newPhotoData);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSearchChange = (event) => {
    updateSearchTerm(event.target.value);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      clearSearch();
    }
  };

  const handleClearSearch = () => {
    clearSearch();
  };

  const handleOpenSettings = () => {
    setOpenSettings(true);
    handleClose(); // Fechar o menu ao abrir as configurações
  };

  const handleCloseSettings = () => {
    setOpenSettings(false);
  };

  // Função wrapper para logout que fecha o menu
  const handleLogoutWithClose = () => {
    handleClose(); // Fechar o menu antes de fazer logout
    handleLogout(); // Executar logout
  };

  return (
    <AppBar
      position="fixed"
      sx={getAppBarStyles()}
    >
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="open drawer"
          onClick={onToggleSidebar}
          sx={{ mr: COMMON_VALUES.standardMargin }}
        >
          <MenuIcon />
        </IconButton>
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 'bold' }}
        >
          AuthSystem
        </Typography>

        <Search>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder="Pesquisar Módulos"
            inputProps={{ 'aria-label': 'search' }}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
          />
          {searchTerm && (
            <IconButton
              size="small"
              onClick={handleClearSearch}
              sx={{
                ...getAbsoluteCenterStyles(),
                color: 'inherit',
                '&:hover': {
                  backgroundColor: getHoverBackground(),
                }
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Search>
        <Box sx={{ flexGrow: 1 }} /> {/* Espaçador */}
        <Box sx={{
          ...getFlexColumnStyles('flex-end'),
          mr: COMMON_VALUES.standardMargin,
          minWidth: COMMON_VALUES.zeroMinWidth // Permite que o container seja comprimido
        }}>
          {/* Componentes visuais */}
          <Box sx={getFlexBoxStyles(1)}>
            {/* Agenda Button e Popover */}
            <Tooltip title="Agenda">
              <IconButton
                size="large"
                aria-label="agenda"
                onClick={handleAgendaClick}
                color="inherit"
                sx={{
                  '&:hover': { backgroundColor: getButtonHoverBackground() },
                  '& .MuiSvgIcon-root': { color: theme.palette.warning?.main || "#ff9800" } // Amarelo/Laranja
                }}
              >
                <Badge badgeContent={eventosHojeCount} color="error">
                  <CalendarMonthIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Popover
              open={openAgenda}
              anchorEl={agendaAnchorEl}
              onClose={handleAgendaClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <div style={{ padding: '8px' }}>
                <AgendaComponent onEventsChange={fetchAgendaHoje} />
              </div>
            </Popover>

            <Tooltip
              title={unreadCount > 0 ? `${unreadCount} novas mensagens` : "Chat Corporativo"}
              placement="bottom"
              arrow
              sx={getTooltipStyles()}
            >
              <IconButton
                size="large"
                aria-label="show messages"
                color="inherit"
                onClick={() => { resetUnread(); navigate('/chat'); }}
                sx={{
                  opacity: 1,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: getButtonHoverBackground() },
                  '& .MuiSvgIcon-root': {
                    color: theme.palette.warning?.main || "#ff9800"
                  }
                }}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <MessageIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Nome e Email do usuário à direita do sino */}
            {usuario?.email && (
              <Box sx={{
                display: { xs: 'none', md: 'flex' },
                ...getFlexColumnStyles('flex-start'),
                maxWidth: '200px',
                minWidth: COMMON_VALUES.zeroMinWidth,
                overflow: 'hidden',
                cursor: COMMON_VALUES.defaultCursor,
                px: COMMON_VALUES.standardPadding,
                py: COMMON_VALUES.smallPadding,
                borderRadius: COMMON_VALUES.standardBorderRadius,
                backgroundColor: getStandardBackground(),
                '&:hover': {
                  backgroundColor: getStandardHoverBackground(),
                }
              }}>
                {/* Nome completo do usuário */}
                <Typography
                  variant="caption"
                  sx={{
                    ...getTextEllipsisStyles('11px', 600),
                    mb: COMMON_VALUES.smallMarginBottom
                  }}
                >
                  {usuario?.nome_completo || "Usuário"}
                </Typography>

                {/* Email do usuário */}
                <Box sx={{
                  ...getFlexBoxStyles(0.3),
                  width: COMMON_VALUES.fullWidth
                }}>
                  <EmailIcon sx={{ fontSize: COMMON_VALUES.smallIconSize, opacity: COMMON_VALUES.smallOpacity }} />
                  <Typography
                    variant="caption"
                    sx={getTextEllipsisStyles('10px', 400, 0.8)}
                  >
                    {usuario.email}
                  </Typography>
                </Box>
              </Box>
            )}
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
              sx={{
                backgroundColor: getStandardBackground(),
                '&:hover': {
                  backgroundColor: getButtonHoverBackground(),
                }
              }}
            >
              <Avatar
                src={userPhoto?.photoUrl || userPhoto || undefined}
                sx={{
                  width: COMMON_VALUES.avatarSize,
                  height: COMMON_VALUES.avatarSize,
                  bgcolor: userPhoto?.photoUrl
                    ? 'transparent'
                    : getThemeColor(theme.palette.secondary.dark, theme.palette.secondary.light),
                  border: userPhoto?.photoUrl
                    ? `2px solid ${getThemeColor(theme.palette.primary.light, theme.palette.primary.main)}`
                    : 'none'
                }}
              >
                {!userPhoto?.photoUrl && (usuario?.nome_completo?.charAt(0)?.toUpperCase() || "U")}
              </Avatar>
            </IconButton>
          </Box>
        </Box>
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          {...getMenuConfig()}
        >
          {renderMenuItemWithIcon(<SettingsIcon />, "Preferências", handleOpenSettings, "#4caf50")} {/* Verde para preferências */}
          <MenuItem onClick={handleClose}>
            {usuario?.nome_completo || "Usuário"}
          </MenuItem>
          {renderMenuItemWithIcon(<ExitToAppIcon />, "Sair", handleLogoutWithClose, "#f44336")} {/* Vermelho para sair */}
        </Menu>
        {/* Modal de Preferências */}
        <SettingsModal
          open={openSettings}
          onClose={handleCloseSettings}
          onPhotoUpdate={handlePhotoUpdate}
        />
      </Toolbar>
    </AppBar>
  );
}

export default Header;
